import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Switch from '../../../components/ui/Switch';
import { InfoTooltip } from '../../../components/ui/Tooltip';
import { Award } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import SkillsInventorySection from './components/SkillsInventorySection';
import StrengthClustersSection from './components/StrengthClustersSection';
import SkillRoleGapsSection from './components/SkillRoleGapsSection';
import { getCompanySkillsInventory, getCompanyJobTitles, createSkill, getCompanyStrengthClusters } from '../../../api/Employee/skills';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle,
} from '../../../components/ui/Dialog';
import { getDepartments } from '../../../api/General/Company';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { useCompanyCapabilities } from '../../../hooks/useCompanyCapabilities';
import { SkillInventoryView, SkillFilterInput, StrengthClusterView } from '../../../models/employee';
import { Department } from '../../../models/general';

type ViewMode = 'overview' | 'skills' | 'strengths' | 'gaps';

interface SkillInventoryItem {
  skillId: number;
  skill: string;
  totalEmployees: number;
  byProficiency: {
    strong: number;
    working: number;
    awareness: number;
  };
  departments: string[];
  trend: 'increasing' | 'decreasing' | 'stable';
}

/*
 * CO-447 — the local `StrengthCluster` interface that sat here (duplicated in
 * `components/StrengthClustersSection.tsx`) is replaced by `StrengthClusterView` from
 * `models/employee.ts`. It was a RENAME, not an import swap: `strength` -> `label`,
 * `frequency` -> `peopleCount`. Neither copy ever matched the server, and neither was ever
 * populated, so nothing caught the mismatch.
 */

const HRSkillsStrengthsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = useSelector((state: any) => state.user.company?.id);
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthorSkills = capabilitiesLoaded && can('canAuthorSkills');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedProficiency, setSelectedProficiency] = useState('all');
  const [gapsOnly, setGapsOnly] = useState(false);
  // `searchInput` is what the field shows; `searchTerm` is what the lists read, one debounce behind.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [jobTitles, setJobTitles] = useState<string[]>([]);

  // Data from API
  const [skillsInventory, setSkillsInventory] = useState<SkillInventoryItem[]>([]);
  const [exportNotice, setExportNotice] = useState('');
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [addSkillError, setAddSkillError] = useState('');
  const [addingSkill, setAddingSkill] = useState(false);

  // CO-447 — real clusters. This was `useState<StrengthCluster[]>([])` with NO setter, so the
  // "Strength Clusters" tab and the "Identified Strengths" summary card have read 0 on every
  // company since the page shipped.
  const [allStrengthClusters, setAllStrengthClusters] = useState<StrengthClusterView[]>([]);
  const [clusterSuppressionRate, setClusterSuppressionRate] = useState<number | undefined>(undefined);

  // Employees whose role/goal skill requirements are under-covered.
  const [underCoveredCount, setUnderCoveredCount] = useState(0);
  // False when the gap endpoint is unavailable to the caller (Manager/Chief 403) — so the
  // summary card shows "—" instead of a misleading 0.
  const [underCoveredKnown, setUnderCoveredKnown] = useState(true);

  useEffect(() => {
    const view = searchParams.get('view');
    const valid: ViewMode[] = ['overview', 'skills', 'strengths', 'gaps'];
    if (view && (valid as string[]).includes(view)) setViewMode(view as ViewMode);
    // run once on mount to honor a ?view=gaps deep-link from the Q3 drill-down
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load departments and job titles on mount
  useEffect(() => {
    const loadFilters = async () => {
      if (!companyId) return;
      try {
        // Load departments
        const deptRes = await getDepartments(companyId);
        setDepartments(deptRes?.payload ?? []);

        // Load job titles
        const titlesRes = await getCompanyJobTitles();
        if (titlesRes.status === 200 && titlesRes.payload) {
          setJobTitles(titlesRes.payload);
        }
      } catch (e) {
        console.error('Failed to load filters', e);
      }
    };
    loadFilters();
  }, [companyId]);

  // Build department options from API data
  const departmentOptions = [
    { value: 'all', label: t('All Departments') },
    ...departments.map(d => ({ value: d.id.toString(), label: d.name }))
  ];

  const roleOptions = [
    { value: 'all', label: t('All Roles') },
    ...jobTitles.map(title => ({ value: title, label: title }))
  ];

  // Values are the byProficiency bucket names, so filtering needs no enum translation.
  const proficiencyOptions = [
    { value: 'all', label: t('skillsOverview.filter.anyProficiency') },
    { value: 'strong', label: t('Strong') },
    { value: 'working', label: t('Working') },
    { value: 'awareness', label: t('Awareness') },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const search = searchTerm.toLowerCase();

  // gapsOnly only counts on the tab that owns its toggle, so the other tabs cannot report a
  // filter the user has no control to clear there.
  const filtersActive = search.length > 0
    || selectedDepartment !== 'all'
    || selectedRole !== 'all'
    || selectedProficiency !== 'all'
    || (viewMode === 'gaps' && gapsOnly);

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedDepartment('all');
    setSelectedRole('all');
    setSelectedProficiency('all');
    setGapsOnly(false);
  };

  // Clusters are company-wide and are NOT re-fetched per filter: `GetCompanyStrengthClusters`
  // takes no parameters (spec §3.3.1), so the department filter is applied to the result below.
  useEffect(() => {
    const loadClusters = async () => {
      try {
        const result = await getCompanyStrengthClusters();
        if (result.status === 200 && result.payload) {
          setAllStrengthClusters(result.payload.clusters);
          setClusterSuppressionRate(
            result.payload.hasSuppressionSummary ? result.payload.suppressionRate : undefined,
          );
        }
      } catch (error) {
        // Same reasoning as the strengths list on the profile: an empty cluster set is a real and
        // common state on a young company, so a failure renders identically. Do not invent a
        // failure banner for a section that is legitimately empty most of the time.
        console.error('Failed to load strength clusters:', error);
      }
    };
    loadClusters();
  }, []);

  /*
   * Department filter, applied client-side.
   *
   * 🔴 The role/job-title filter is NOT applied to clusters, deliberately. `StrengthClusterView`
   * carries `departments` but no job titles, so there is nothing to filter on — silently returning
   * the unfiltered list under an active role filter would be a lie, and inventing a client-side
   * approximation would be a worse one. Filtering by job title needs a server-side parameter on
   * `GetCompanyStrengthClusters`; recorded as a follow-up rather than faked here.
   */
  const selectedDepartmentName = selectedDepartment !== 'all'
    ? departments.find((d) => d.id.toString() === selectedDepartment)?.name
    : undefined;

  const strengthClusters = useMemo(() => {
    let list = selectedDepartmentName
      ? allStrengthClusters.filter((c) => c.departments?.includes(selectedDepartmentName))
      : allStrengthClusters;
    // A cluster's `label` is the only name it carries, so that is what the search matches.
    if (search) list = list.filter((c) => c.label?.toLowerCase().includes(search));
    return list;
  }, [allStrengthClusters, selectedDepartmentName, search]);

  /*
   * The department filter is served by the API (`loadSkillsInventory`); search and proficiency are
   * applied here on the loaded rows so typing does not re-request. Proficiency keeps the skills that
   * SOMEBODY holds at that level, leaving each row's full distribution visible — passing it to
   * `SkillFilterInput.proficiency` would instead re-aggregate the row and zero out the other two
   * bars, which reads as data loss.
   */
  const visibleInventory = useMemo(() => {
    let list = skillsInventory;
    if (search) list = list.filter((s) => s.skill?.toLowerCase().includes(search));
    if (selectedProficiency !== 'all') {
      const bucket = selectedProficiency as keyof SkillInventoryItem['byProficiency'];
      list = list.filter((s) => (s.byProficiency?.[bucket] ?? 0) > 0);
    }
    return list;
  }, [skillsInventory, search, selectedProficiency]);

  useEffect(() => {
    loadSkillsInventory();
    // NOTE: the under-covered count is NOT fetched here. <SkillRoleGapsSection> already calls
    // getSkillRoleGaps with this exact departmentId on every render of the overview, so issuing
    // a second identical request here double-fetched the endpoint on mount and on every
    // department change (CO-205). The section reports its result up via `onGapsLoaded`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);

  // Fed by <SkillRoleGapsSection>; `null` means the caller may not read the gap endpoint
  // (Manager/Chief 403) or it failed, in which case the card shows an em-dash instead of 0.
  const handleGapsLoaded = useCallback((count: number | null) => {
    if (count == null) {
      setUnderCoveredKnown(false);
      return;
    }
    setUnderCoveredCount(count);
    setUnderCoveredKnown(true);
  }, []);

  const loadSkillsInventory = async () => {
    setLoading(true);
    try {
      const filter: SkillFilterInput = {};
      if (selectedDepartment !== 'all') {
        filter.departmentId = parseInt(selectedDepartment, 10);
      }

      const result = await getCompanySkillsInventory(filter);
      if (result.status === 200 && result.payload) {
        // Transform API data to match the UI component format
        const transformed: SkillInventoryItem[] = result.payload.map((item: SkillInventoryView) => ({
          skillId: item.skillId,
          skill: item.skillName,
          totalEmployees: item.totalEmployees,
          byProficiency: item.byProficiency,
          departments: item.departments,
          trend: 'stable' as const // TODO: Implement trend tracking
        }));
        setSkillsInventory(transformed);
      }
    } catch (error) {
      console.error('Failed to load skills inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  // Adds the skill to the company catalogue with nobody assigned. The inventory now lists unheld
  // catalogue skills at zero, so reload it here: without the reload the dialog just closed and the
  // list looked unchanged, which is indistinguishable from the add having failed.
  const handleAddSkill = async () => {
    const name = newSkillName.trim();
    if (!canAuthorSkills) return;
    if (!name || addingSkill) return;
    setAddingSkill(true);
    setAddSkillError('');
    try {
      const result = await createSkill({ name });
      if (result.errors.length > 0 || !result.payload) {
        setAddSkillError(result.errors[0]?.description || t('skillsOverview.addSkillFailed'));
        return;
      }
      setShowAddSkill(false);
      setNewSkillName('');
      await loadSkillsInventory();
    } catch {
      setAddSkillError(t('skillsOverview.addSkillFailed'));
    } finally {
      setAddingSkill(false);
    }
  };

  // Quotes every field: skill and department names legitimately contain commas.
  const toCsvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

  // Exports what the page is showing, filters included, so the file matches the screen.
  const handleExport = () => {
    if (visibleInventory.length === 0) {
      setExportNotice(t('skillsOverview.exportEmpty'));
      return;
    }
    setExportNotice('');

    const header = [
      t('skillsOverview.csvSkill'), t('skillsOverview.csvTotalEmployees'),
      t('skillsOverview.csvStrong'), t('skillsOverview.csvWorking'),
      t('skillsOverview.csvAwareness'), t('skillsOverview.csvDepartments'),
    ];
    const rows = visibleInventory.map((s) => [
      s.skill, s.totalEmployees, s.byProficiency.strong, s.byProficiency.working,
      s.byProficiency.awareness, s.departments.join('; '),
    ]);

    // The BOM is what makes Excel read this as UTF-8; without it Arabic skill names arrive mangled.
    const csv = '﻿' + [header, ...rows].map((r) => r.map(toCsvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `skills-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    loadSkillsInventory();
  };

  return (
    <PageLayout
      title={t('Skills & Strengths Overview')}
      description={t('Organizational capability analytics and workforce development insights')}
      icon={Award}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="RefreshCw"
            onClick={handleRefresh}
            loading={loading}
          >
            {t('Refresh')}
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            onClick={handleExport}
          >
            {t('Export Report')}
          </Button>
          {/* Outline like every other page-header action in this app (Assets "Add asset",
              Team Roles "Create role"), and like the two buttons beside it. The default variant's
              primary fill made this the only solid button in any header, and its hover was a 10%
              tint nobody can see. */}
          {canAuthorSkills && (
            <Button
              variant="outline"
              iconName="Plus"
              onClick={() => {
                if (!canAuthorSkills) return;
                setNewSkillName(''); setAddSkillError(''); setShowAddSkill(true);
              }}
              data-testid="add-skill-button"
            >
              {t('skillsOverview.addSkill')}
            </Button>
          )}
        </div>
      }
    >
        <ManagedElsewhereNotice capability="canAuthorSkills" className="mb-4" />

        {exportNotice && (
          <div className="mb-4 rounded-lg border border-warning/20 bg-warning/5 px-4 py-3 text-sm text-warning" data-testid="skills-export-notice">
            {exportNotice}
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 border-b border-border">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'overview' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="LayoutDashboard" size={16} />
                <span>{t('Overview')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('skills')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'skills' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} />
                <span>{t('Skills Inventory')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('strengths')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'strengths' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Sparkles" size={16} />
                <span>{t('Strength Clusters')}</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('gaps')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'gaps' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} />
                <span>{t('Gap Analysis')}</span>
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-card rounded-lg shadow-sm border border-border p-4">
          {/* One row, label first and its controls immediately after: `justify-between` used to push
              the two apart and leave a dead gap between them on wide screens. */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Icon name="Filter" size={16} />
              {t('Filters:', { nsSeparator: false })}
            </span>

            <div className="relative w-full sm:w-48">
              <Icon
                name="Search"
                size={16}
                className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('skillsOverview.searchPlaceholder')}
                aria-label={t('skillsOverview.searchPlaceholder')}
                className="h-10 w-full rounded-md border border-border bg-background ps-9 pe-9 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="skills-search"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label={t('Clear')}
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  data-testid="skills-search-clear"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>

          </div>

          {/* The dropdowns get their own row and never shrink or wrap, so they always read as one
              group. If the viewport is too narrow for all of them the row scrolls sideways rather
              than stacking them one per line. */}
          <div className="mt-3 flex items-center gap-3 overflow-x-auto pb-1">
            <Select
              options={departmentOptions}
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              placeholder={t('Select department')}
              className="w-44 shrink-0"
            />
            <Select
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
              placeholder={t('Select role level')}
              className="w-44 shrink-0"
            />
            {(viewMode === 'overview' || viewMode === 'skills') && (
              <Select
                options={proficiencyOptions}
                value={selectedProficiency}
                onChange={setSelectedProficiency}
                placeholder={t('skillsOverview.filter.anyProficiency')}
                className="w-44 shrink-0"
                data-testid="skills-proficiency-filter"
              />
            )}
            {viewMode === 'gaps' && (
              <div className="flex shrink-0 items-center gap-1.5" data-testid="gaps-only-toggle">
                <Switch
                  id="gaps-only-switch"
                  checked={gapsOnly}
                  onCheckedChange={setGapsOnly}
                  label={t('skillGaps.filter.gapsOnly')}
                />
                <InfoTooltip
                  content={t('skillGaps.filter.gapsOnlyTooltip')}
                  label={t('skillGaps.filter.gapsOnly')}
                  data-testid="gaps-only-tooltip"
                />
              </div>
            )}
            {filtersActive && (
              /* Kept as a labelled button rather than a bare icon: a refresh glyph reads as
                 "reload the data", not "clear the filters". `ms-auto` sets it apart from the
                 controls it clears, and collapses harmlessly if the row ever has to scroll. */
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={clearFilters}
                className="ms-auto shrink-0"
                data-testid="skills-clear-filters"
              >
                {t('Clear filters')}
              </Button>
            )}
          </div>
        </div>

        {/* Content Based on View Mode */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={24} className="text-blue-600" />
                  </div>
                  <Icon name="TrendingUp" size={20} className="text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{visibleInventory?.length}</h3>
                <p className="text-sm text-gray-600">{t('Tracked Skills')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('Across {{count}} departments', { count: departments.length || departmentOptions?.length - 1 })}</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name="Sparkles" size={24} className="text-purple-600" />
                  </div>
                  <Icon name="TrendingUp" size={20} className="text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1" data-testid="identified-strengths-count">{strengthClusters?.length}</h3>
                <p className="text-sm text-gray-600">{t('Identified Strengths')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('{{count}} emerging trends', { count: strengthClusters?.filter(s => s?.emergingTrend)?.length })}</p>
              </div>

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={24} className="text-orange-600" />
                  </div>
                  <Icon name="AlertCircle" size={20} className="text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{underCoveredKnown ? underCoveredCount : '—'}</h3>
                <p className="text-sm text-gray-600">{t('skillGaps.card.underCovered')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('skillGaps.card.underCoveredHint')}</p>
              </div>
            </div>

            {/* All Sections with View More navigation */}
            <SkillsInventorySection
              data={visibleInventory}
              filtersActive={filtersActive}
              onClearFilters={clearFilters}
              onViewMore={() => setViewMode('skills')}
            />
            <StrengthClustersSection
              data={strengthClusters}
              suppressionRate={clusterSuppressionRate}
              onViewMore={() => setViewMode('strengths')}
            />
            {/* The gaps-only toggle lives on the Gap Analysis tab, so overview never filters by it. */}
            <SkillRoleGapsSection
              departmentId={selectedDepartment !== 'all' ? parseInt(selectedDepartment, 10) : undefined}
              searchTerm={searchTerm}
              gapsOnly={false}
              onGapsLoaded={handleGapsLoaded}
              onFixEmployee={(employeeId) => navigate(`/profile/${employeeId}/skills`)}
            />
          </div>
        )}

        {viewMode === 'skills' && (
          <SkillsInventorySection
            data={visibleInventory}
            filtersActive={filtersActive}
            onClearFilters={clearFilters}
            expanded
          />
        )}
        {viewMode === 'strengths' && (
          <StrengthClustersSection data={strengthClusters} suppressionRate={clusterSuppressionRate} expanded />
        )}
        {viewMode === 'gaps' && (
          <SkillRoleGapsSection
            departmentId={selectedDepartment !== 'all' ? parseInt(selectedDepartment, 10) : undefined}
            searchTerm={searchTerm}
            gapsOnly={gapsOnly}
            onFixEmployee={(employeeId) => navigate(`/profile/${employeeId}/skills`)}
          />
        )}

        <Dialog open={showAddSkill && canAuthorSkills} onOpenChange={(o) => !o && !addingSkill && setShowAddSkill(false)}>
          <DialogContent
            onEscapeKeyDown={(e) => { if (addingSkill) e.preventDefault(); }}
            onInteractOutside={(e) => { if (addingSkill) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>{t('skillsOverview.addSkill')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className="mb-3 text-sm text-muted-foreground">{t('skillsOverview.addSkillHint')}</p>
              <input
                type="text"
                value={newSkillName}
                onChange={(e) => { setNewSkillName(e.target.value); setAddSkillError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddSkill(); } }}
                placeholder={t('skillsOverview.addSkillPlaceholder')}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="add-skill-input"
              />
              {addSkillError && <p className="mt-2 text-sm text-destructive">{addSkillError}</p>}
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" disabled={addingSkill} onClick={() => setShowAddSkill(false)}>
                {t('Cancel')}
              </Button>
              <Button
                onClick={handleAddSkill}
                disabled={!newSkillName.trim() || addingSkill}
                loading={addingSkill}
                data-testid="add-skill-submit"
              >
                {t('skillsOverview.addSkillSubmit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </PageLayout>
  );
};

export default HRSkillsStrengthsOverview;
